import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { persistQueryClient } from '@tanstack/query-persist-client-core'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { RouterProvider } from 'react-router-dom'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { Toaster } from '@/components/ui/toaster'
import { initTheme } from '@/hooks/useTheme'
import { router } from '@/routes'
import './index.css'

initTheme()

const CACHE_VERSION = 'v2'
const CACHE_KEY = 'statiomart-pos-query-cache'
const VERSION_KEY = 'sm-pos-cache-version'

if (localStorage.getItem(VERSION_KEY) !== CACHE_VERSION) {
  localStorage.removeItem(CACHE_KEY)
  localStorage.setItem(VERSION_KEY, CACHE_VERSION)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 3,
    },
    mutations: {
      retry: 1,
    },
  },
})

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: CACHE_KEY,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
persistQueryClient({
  queryClient: queryClient as any,
  persister,
  maxAge: 24 * 60 * 60 * 1000,
  dehydrateOptions: {
    // Serial gaps must always reflect the live DB — never resurrect a stale
    // day-old snapshot on page load (see billing.service.ts listSerialGaps).
    shouldDehydrateQuery: (query) => query.queryKey[0] !== 'serial-gaps',
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary onError={(error) => console.error('POS Error:', error)}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
)
